import { useState, useEffect, useCallback } from "react";
import RlinkWhiteLogo from "../../images/branding/rlink-white-logo.png";
import { useSiteContext } from "../../context/SiteContext";
import userServices from "../../services/userServices";
import { toast } from "react-toastify";
import { getTimezoneZone, TIMEZONES } from "../../utils/timezones";

function WebinerBrandingPre() {
  const { meetingSettings } = useSiteContext();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });

  const [formErrors, setFormErrors] = useState({
    name: "",
    email: "",
    phone: "",
  });

  const [isRegistered, setIsRegistered] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  const calculateTimeDifference = useCallback(() => {
    if (!meetingSettings?.scheduledStartTime || !meetingSettings?.timezone) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }
    const meetingTimeISO = meetingSettings.scheduledStartTime + ".000Z";

    // browser timezone
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // conver time to browser timezone
    const meetingTimeInBrowserTimezone = new Date(
      meetingTimeISO
    ).toLocaleString("en-US", {
      timeZone: browserTimezone,
    });

    if (!meetingTimeInBrowserTimezone) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    try {
      // Convert meeting time to browser timezone and get difference to browser current time

      // Parse the scheduled meeting time as UTC, then render it in browser's timezone
      const meetingTimeUTC = new Date(meetingTimeISO);

      // Get the browser's current time (local system time)
      const now = new Date();

      // Calculate meeting start time in the browser timezone ('wall clock time' for the user)
      // First, get the same "wall clock time" as the browser would show:
      // String in "MM/DD/YYYY, HH:mm:ss AM/PM" format in browser timezone
      const meetingTimeInBrowserString = meetingTimeUTC.toLocaleString(
        "en-US",
        {
          timeZone: browserTimezone,
        }
      );

      // Parse that "wall clock" time string back into a Date (browser will interpret as local time)
      const meetingTimeInBrowserDate = new Date(meetingTimeInBrowserString);

      // Get diff in ms: meetingTime in browser - now
      const diffMs = meetingTimeInBrowserDate.getTime() - now.getTime();

      if (diffMs <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };

      // Calculate days, hours, minutes, seconds
      const totalSec = Math.floor(diffMs / 1000);
      const days = Math.floor(totalSec / (3600 * 24));
      const hours = Math.floor((totalSec % (3600 * 24)) / 3600);
      const minutes = Math.floor((totalSec % 3600) / 60);
      const seconds = totalSec % 60;

      if (seconds <= 0) {
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      }

      return { days, hours, minutes, seconds };
    } catch (err) {
      console.error("Error calculating time difference:", err);
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }
  }, [meetingSettings?.scheduledStartTime, meetingSettings?.timezone]);

  useEffect(() => {
    // Calculate initial time difference
    setTimeLeft(calculateTimeDifference());

    // Update countdown every second
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeDifference());
    }, 1000);

    return () => clearInterval(timer);
  }, [calculateTimeDifference]);

  // Validation functions
  const validateName = (name) => {
    if (!name.trim()) {
      return "Name is required";
    }
    if (name.trim().length < 2) {
      return "Name must be at least 2 characters";
    }
    if (!/^[a-zA-Z\s]+$/.test(name.trim())) {
      return "Name can only contain letters and spaces";
    }
    return "";
  };

  const validateEmail = (email) => {
    if (!email.trim()) {
      return "Email is required";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      return "Please enter a valid email address";
    }
    return "";
  };

  const validatePhone = (phone) => {
    // Phone is optional, so if empty, return no error
    if (!phone.trim()) {
      return "";
    }
    // Remove all non-digit characters for validation
    const phoneDigits = phone.replace(/\D/g, "");
    if (phoneDigits.length < 10) {
      return "Phone number must be at least 10 digits";
    }
    if (phoneDigits.length > 15) {
      return "Phone number cannot exceed 15 digits";
    }
    return "";
  };

  const validateForm = () => {
    const errors = {
      name: validateName(formData.name),
      email: validateEmail(formData.email),
      phone: validatePhone(formData.phone),
    };

    setFormErrors(errors);
    return !Object.values(errors).some((error) => error !== "");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await userServices.preRegister({
        ...formData,
        meetingId: meetingSettings?.roomId,
      });

      if (response.success) {
        setIsRegistered(true);
        toast.success("Registration successful");
        setFormData({
          name: "",
          email: "",
          phone: "",
        });
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error("Registration error:", error);
      alert("Network error. Please check your connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendEmail = () => {
    console.log("Resend email to:", formData.email);
  };

  const handleChangeEmail = () => {
    setIsRegistered(false);
  };

  const formatTime = (time) => String(time).padStart(2, "0");

  return (
    <div className="flex flex-col min-h-screen bg-black">
      {/* Header */}
      <header className="flex justify-between items-center h-[60px] px-8 border-b border-[#2B2A3C] bg-black">
        <div className="flex items-center">
          <svg
            className="w-[35px] h-[32px]"
            viewBox="0 0 35 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M22.8881 17.545C26.9885 17.1158 28.7936 12.5266 26.8103 9.19201C24.7489 5.72537 20.147 6.91393 16.7932 6.77086C16.2695 4.15162 15.0326 2.03862 12.8376 0.464879C16.8712 0.894082 21.0384 0.299801 25.072 0.773025C29.4956 1.27926 32.4372 3.0291 33.3175 7.55224C34.0306 11.261 33.9749 17.512 30.8216 20.1532C30.4761 20.4393 29.0833 21.1437 29.139 21.3638L35 31.2905L26.632 31.2244L21.2724 22.3212H16.7932L16.6483 17.622C16.8377 17.2918 16.9603 17.545 17.016 17.545C18.8211 17.545 21.161 17.7211 22.8881 17.545Z"
              fill="white"
            />
            <path
              d="M15.1886 25.7218C14.3195 33.2934 2.61989 34.438 0.413675 26.8334C-0.121165 24.9845 -0.165734 18.0292 0.447103 16.2353C1.61707 12.8457 5.32752 10.6667 8.92655 11.349C9.81795 11.5141 9.5171 14.0563 9.09368 14.6396C8.16886 15.9272 5.18267 15.52 4.80382 18.0842C4.60326 19.4819 4.58097 23.873 4.78154 25.2706C5.08238 27.4937 8.32485 28.231 9.84023 26.6573C10.9768 25.4687 10.4754 23.0806 10.7093 21.5839L13.2053 20.1312L15.1886 17.9632C14.9881 20.4173 15.4672 23.3007 15.1886 25.6998V25.7218Z"
              fill="white"
            />
            <path
              d="M5.94036 17.9081C5.94036 16.7086 10.0408 16.4995 10.4531 14.2544C10.7428 12.7027 10.7985 8.91688 10.6202 7.35414C10.3639 5.1421 8.30257 3.94253 6.23006 4.87797C4.04613 5.85743 5.01553 8.61974 4.6924 10.4576C2.74246 11.261 1.4165 12.5156 0.0682592 14.0783V6.48473C0.0682592 3.44729 3.83442 0.343822 6.7649 0.0576863C15.3892 -0.789715 15.6789 7.88239 15.1886 14.0123C14.8098 18.7996 10.5868 21.4738 5.94036 20.7365V17.9081Z"
              fill="white"
            />
          </svg>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-white font-montserrat text-xs">Sign Up</span>
          <svg
            className="w-5 h-5"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 0C4.47518 0 0 4.47519 0 10C0 12.1565 0.686968 14.1508 1.85109 15.7824C3.66407 18.3302 6.63167 20 10 20C13.3683 20 16.3359 18.3302 18.1489 15.7824C19.313 14.1508 20 12.1565 20 10C20 4.47519 15.5248 0 10 0ZM6.5649 7.7958C6.5649 5.89695 8.10114 4.36069 10 4.36069C11.8989 4.36069 13.4351 5.89695 13.4351 7.7958C13.4351 7.80535 13.4351 7.81489 13.4351 7.83397C13.4351 7.85305 13.4351 7.85305 13.4351 7.87214C13.4351 9.77099 11.8989 11.3073 10 11.3073C8.10114 11.3073 6.5649 9.77099 6.5649 7.87214C6.5649 7.8626 6.5649 7.85305 6.5649 7.83397C6.5649 7.81489 6.5649 7.81489 6.5649 7.7958ZM16.813 14.8855C15.2863 17.0038 12.8053 18.3969 10 18.3969C7.19465 18.3969 4.71372 17.0134 3.187 14.8855C3.19654 14.8855 3.21566 14.876 3.2252 14.8664C3.21566 14.8473 3.19654 14.8378 3.187 14.8187C5.18127 13.6355 7.50954 12.9485 10 12.9485C12.4905 12.9485 14.8187 13.6355 16.813 14.8187C16.8035 14.8378 16.7843 14.8473 16.7748 14.8664C16.7843 14.8664 16.8035 14.876 16.813 14.8855Z"
              fill="#7F7F7F"
            />
          </svg>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex flex-col lg:flex-row flex-1">
        {/* Left Side - Image */}
        <div className="w-full flex justify-center items-center lg:w-[55%] h-[300px] lg:h-auto">
          {meetingSettings && meetingSettings.registrationCoverImage ? (
            <img src={meetingSettings.registrationCoverImage} alt="" />
          ) : (
            <img src={"/webinarlogo.png"} />
          )}
        </div>

        {/* Right Side - Registration Form */}
        <div className="flex-1 flex flex-col justify-center items-center px-6 py-10 lg:px-12 lg:py-8">
          <div className="max-w-[531px] w-full flex flex-col gap-10">
            {/* Title Section */}
            <div className="flex flex-col items-center gap-2.5">
              <h1
                className="text-white text-center font-bold text-2xl lg:text-[26px] leading-tight break-words w-full"
                style={{
                  fontFamily: "'Open Sans', sans-serif",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {meetingSettings && meetingSettings.registrationTitle
                  ? meetingSettings.registrationTitle
                  : meetingSettings.roomName}
              </h1>
              {meetingSettings && meetingSettings.registrationSubtitle && (
                <h2
                  className="text-white text-center font-bold text-2xl lg:text-[26px] leading-tight"
                  style={{ fontFamily: "'Open Sans', sans-serif" }}
                >
                  {meetingSettings.registrationSubtitle}
                </h2>
              )}
            </div>

            {/* Event Time */}
            <div className="flex flex-col items-center">
              <p
                className="text-[#00C853] text-sm font-medium"
                style={{ fontFamily: "'Open Sans', sans-serif" }}
              >
                Event Time
              </p>
              <p
                className="text-[#00C853] text-lg font-medium"
                style={{ fontFamily: "'Open Sans', sans-serif" }}
              >
                {meetingSettings?.scheduledStartTime
                  ? (() => {
                      try {
                        // Use Intl.DateTimeFormat for correct timezone rendering
                        const formatter = new Intl.DateTimeFormat("en-US", {
                          timeZone: meetingSettings.timezone,
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                          hour12: true,
                        });
                        return (
                          formatter.format(
                            new Date(
                              meetingSettings.scheduledStartTime + ".000Z"
                            )
                          ) +
                          " (" +
                          getTimezoneZone(meetingSettings.timezone) +
                          ")"
                        );
                      } catch (e) {
                        return new Date(
                          meetingSettings.scheduledStartTime
                        ).toLocaleString();
                      }
                    })()
                  : "Schedule time not set"}
              </p>
            </div>

            {/* Countdown Timer */}
            {meetingSettings && meetingSettings.countdownTimer && (
              <div className="flex justify-center items-center gap-4 lg:gap-6 flex-wrap">
                <div className="flex flex-col items-center min-w-[60px]">
                  <div className="w-[60px] h-[60px] flex items-center justify-center rounded-[11px] bg-black">
                    <span
                      className="text-[#C6C6C6] text-[33px] font-bold"
                      style={{ fontFamily: "'Open Sans', sans-serif" }}
                    >
                      {formatTime(timeLeft.days)}
                    </span>
                  </div>
                  <span
                    className="text-[#C6C6C6] text-xs text-center mt-1"
                    style={{ fontFamily: "'Open Sans', sans-serif" }}
                  >
                    DAYS
                  </span>
                </div>

                <div className="flex flex-col items-center min-w-[60px]">
                  <div className="w-[60px] h-[60px] flex items-center justify-center rounded-[11px] bg-black">
                    <span
                      className="text-[#C6C6C6] text-[33px] font-bold"
                      style={{ fontFamily: "'Open Sans', sans-serif" }}
                    >
                      {formatTime(timeLeft.hours)}
                    </span>
                  </div>
                  <span
                    className="text-[#C6C6C6] text-xs text-center mt-1"
                    style={{ fontFamily: "'Open Sans', sans-serif" }}
                  >
                    HOURS
                  </span>
                </div>

                <div className="flex flex-col items-center min-w-[60px]">
                  <div className="w-[60px] h-[60px] flex items-center justify-center rounded-[11px] bg-black">
                    <span
                      className="text-[#C6C6C6] text-[33px] font-bold"
                      style={{ fontFamily: "'Open Sans', sans-serif" }}
                    >
                      {formatTime(timeLeft.minutes)}
                    </span>
                  </div>
                  <span
                    className="text-[#C6C6C6] text-xs text-center mt-1"
                    style={{ fontFamily: "'Open Sans', sans-serif" }}
                  >
                    MINUTES
                  </span>
                </div>

                <div className="flex flex-col items-center min-w-[60px]">
                  <div className="w-[60px] h-[60px] flex items-center justify-center rounded-[11px] bg-black">
                    <span
                      className="text-[#C6C6C6] text-[33px] font-bold"
                      style={{ fontFamily: "'Open Sans', sans-serif" }}
                    >
                      {formatTime(timeLeft.seconds)}
                    </span>
                  </div>
                  <span
                    className="text-[#C6C6C6] text-xs text-center mt-1"
                    style={{ fontFamily: "'Open Sans', sans-serif" }}
                  >
                    SECONDS
                  </span>
                </div>
              </div>
            )}

            {/* Registration Form Container */}
            <div
              className="w-full py-8 px-5 lg:py-9 lg:px-0 flex flex-col items-center rounded-[11.5px]"
              style={{ background: "rgba(0, 0, 0, 0.12)" }}
            >
              {!isRegistered && meetingSettings?.requireRegistration ? (
                <>
                  <h3
                    className="text-white text-center text-lg font-medium mb-10"
                    style={{ fontFamily: "'Open Sans', sans-serif" }}
                  >
                    REGISTER TO THIS EVENT
                  </h3>

                  <form
                    onSubmit={handleSubmit}
                    className="w-full max-w-[393px] flex flex-col gap-6"
                  >
                    {/* Name Field */}
                    <div className="flex flex-col gap-1.5">
                      <label
                        htmlFor="name"
                        className="text-white text-sm font-medium font-montserrat"
                      >
                        Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="John"
                        className={`w-full px-4 py-3 rounded-[5px] bg-white text-sm font-montserrat text-gray-900 placeholder:text-[#999] focus:outline-none focus:ring-2 ${
                          formErrors.name
                            ? "focus:ring-red-500 border-red-500"
                            : "focus:ring-[#00C853]"
                        }`}
                      />
                      {formErrors.name && (
                        <span className="text-red-400 text-xs font-montserrat">
                          {formErrors.name}
                        </span>
                      )}
                    </div>

                    {/* Email Field */}
                    <div className="flex flex-col gap-1.5">
                      <label
                        htmlFor="email"
                        className="text-white text-sm font-medium font-montserrat"
                      >
                        E-mail
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="john@maxwellmedia.com"
                        className={`w-full px-4 py-3 rounded-[5px] bg-white text-sm font-montserrat text-gray-900 placeholder:text-[#999] focus:outline-none focus:ring-2 ${
                          formErrors.email
                            ? "focus:ring-red-500 border-red-500"
                            : "focus:ring-[#00C853]"
                        }`}
                      />
                      {formErrors.email && (
                        <span className="text-red-400 text-xs font-montserrat">
                          {formErrors.email}
                        </span>
                      )}
                    </div>

                    {/* Phone Field */}
                    <div className="flex flex-col gap-1.5">
                      <label
                        htmlFor="phone"
                        className="text-white text-sm font-medium font-montserrat"
                      >
                        Phone Number (Optional)
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+1 (573) 235 - 2523"
                        className={`w-full px-4 py-3 rounded-[5px] bg-white text-sm font-montserrat text-gray-900 placeholder:text-[#999] focus:outline-none focus:ring-2 ${
                          formErrors.phone
                            ? "focus:ring-red-500 border-red-500"
                            : "focus:ring-[#00C853]"
                        }`}
                      />
                      {formErrors.phone && (
                        <span className="text-red-400 text-xs font-montserrat">
                          {formErrors.phone}
                        </span>
                      )}
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full max-w-[158px] mx-auto h-[30px] px-4 flex items-center justify-center rounded-[5px] border border-[#00C853] bg-transparent hover:bg-[#00C853] hover:bg-opacity-10 transition-colors ${
                        isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      <span className="text-[#00C853] text-sm font-semibold font-montserrat">
                        {isSubmitting ? "Registering..." : "Register"}
                      </span>
                    </button>
                  </form>
                </>
              ) : meetingSettings?.requireRegistration ? (
                <div className="flex flex-col items-center gap-8 lg:gap-12 w-full max-w-[531px] px-4">
                  <h2
                    className="text-white text-center text-lg font-normal leading-4"
                    style={{ fontFamily: "'Open Sans', sans-serif" }}
                  >
                    THANK YOU FOR REGISTERING
                  </h2>

                  <p
                    className="text-white text-center text-base lg:text-[17px] font-normal leading-normal"
                    style={{ fontFamily: "'Open Sans', sans-serif" }}
                  >
                    Please check the email you provided for
                    <br className="hidden sm:block" />
                    your personal event link
                  </p>

                  <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-10">
                    <button
                      onClick={handleResendEmail}
                      className="min-w-[140px] sm:min-w-[158px] h-[30px] px-8 flex items-center justify-center rounded-[5px] border border-[#00C853] bg-transparent hover:bg-[#00C853] hover:bg-opacity-10 transition-colors"
                    >
                      <span
                        className="text-[#00C853] text-center text-sm font-bold leading-[14px]"
                        style={{ fontFamily: "Montserrat, sans-serif" }}
                      >
                        Resend Email
                      </span>
                    </button>

                    <button
                      onClick={handleChangeEmail}
                      className="min-w-[140px] sm:min-w-[158px] h-[30px] px-8 flex items-center justify-center rounded-[5px] border border-[#00C853] bg-transparent hover:bg-[#00C853] hover:bg-opacity-10 transition-colors"
                    >
                      <span
                        className="text-[#00C853] text-center text-sm font-bold leading-[14px]"
                        style={{ fontFamily: "Montserrat, sans-serif" }}
                      >
                        Change Email
                      </span>
                    </button>
                  </div>
                </div>
              ) : null}
            </div>

            {/* Powered By Footer */}
            <div className="flex justify-center items-center gap-2.5">
              <span
                className="text-white text-[7px] font-medium"
                style={{ fontFamily: "'Open Sans', sans-serif" }}
              >
                POWERED BY
              </span>
              <img className="w-20" src={RlinkWhiteLogo} alt="" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WebinerBrandingPre;
